import ky, { type KyInstance } from 'ky'

import type { BackofficeContext } from '../../framework/context/types'
import type { GridApi, GridApiResponse } from '../../framework/view/types'
import { mapKyHttpError } from '../ky-error'
import { toSearchParams, prepareQueryParams } from '../query'
import {
  parseGridPaginationInfo,
  parseHttpBody,
  parseRecordList,
  type ApiResponse,
} from '../response'

export interface TournamentRoundsApi {
  grid: GridApi
  getSingle: (id: unknown, params?: Record<string, unknown>) => Promise<ApiResponse>
  update: (id: unknown, data: Record<string, unknown>) => Promise<ApiResponse>
  create: (data: Record<string, unknown>) => Promise<ApiResponse>
  delete: (id: unknown) => Promise<void>
  getTranslations: (id: unknown, filters?: Record<string, unknown>) => Promise<ApiResponse>
  getFeedTypeMappings: (id: unknown, filters?: Record<string, unknown>) => Promise<ApiResponse>
  getFeedInstanceMappings: (id: unknown, filters?: Record<string, unknown>) => Promise<ApiResponse>
}

export type TournamentRoundsApiDeps = Pick<BackofficeContext, 'read' | 'auth'>

function gridRequestParams(filters: Record<string, unknown>): Record<string, unknown> {
  const params = prepareQueryParams(filters)
  delete params.searchParameter
  return {
    ...params,
    isReadAfterWrite: filters.isReadAfterWrite || false,
    search: filters.searchParameter ?? filters.search,
  }
}

function readAfterWriteParams(filters: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    isReadAfterWrite: filters.isReadAfterWrite || false,
  }
}

function toGridResponse(body: unknown): GridApiResponse {
  const response = (body ?? {}) as ApiResponse
  return {
    data: parseRecordList(response.data),
    paginationInfo: parseGridPaginationInfo(response),
  }
}

export function createTournamentRoundsApi(
  deps: TournamentRoundsApiDeps,
  http?: KyInstance,
): TournamentRoundsApi {
  const { read, auth } = deps

  const client =
    http ??
    ky.create({
      prefix: `${import.meta.env.NXG_SPORTS_AIO_CORE}/tenants/${read.tenantId()}/instances/${read.instance()}/languages/${read.language()}`,
      retry: 0,
      throwHttpErrors: true,
      headers: { Accept: 'application/json' },
      hooks: {
        beforeRequest: [
          ({ request }) => {
            request.headers.set('Authorization', `Bearer ${auth.getAccessToken()}`)
          },
        ],
        beforeError: [({ error }) => mapKyHttpError(error)],
      },
    })

  return {
    grid: {
      get: async (filters) => {
        const body = parseHttpBody(
          await client
            .get('tournament-rounds/grid', {
              searchParams: toSearchParams(gridRequestParams(filters)),
            })
            .text(),
        )
        return toGridResponse(body)
      },
      update: async (id, params) =>
        parseHttpBody(
          await client
            .patch(`tournament-rounds/${id}`, {
              body: JSON.stringify(params),
              headers: { 'Content-Type': 'application/merge-patch+json' },
            })
            .text(),
        ) as ApiResponse,
      delete: async (id) => {
        await client.delete(`tournament-rounds/${id}`)
      },
    },
    getSingle: async (id, params = {}) =>
      parseHttpBody(
        await client
          .get(`tournament-rounds/${id}`, {
            searchParams: toSearchParams(readAfterWriteParams(params)),
          })
          .text(),
      ) as ApiResponse,
    update: async (id, data) =>
      parseHttpBody(
        await client
          .patch(`tournament-rounds/${id}`, {
            body: JSON.stringify(data),
            headers: { 'Content-Type': 'application/merge-patch+json' },
          })
          .text(),
      ) as ApiResponse,
    create: async (data) =>
      parseHttpBody(await client.post('tournament-rounds', { json: data }).text()) as ApiResponse,
    delete: async (id) => {
      await client.delete(`tournament-rounds/${id}`)
    },
    getTranslations: async (id, filters = {}) =>
      parseHttpBody(
        await client
          .get(`tournament-rounds/${id}/translations`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse,
    getFeedTypeMappings: async (id, filters = {}) =>
      parseHttpBody(
        await client
          .get(`tournament-rounds/${id}/feed-type-mappings`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse,
    getFeedInstanceMappings: async (id, filters = {}) =>
      parseHttpBody(
        await client
          .get(`tournament-rounds/${id}/feed-instance-mappings`, {
            searchParams: toSearchParams(readAfterWriteParams(filters)),
          })
          .text(),
      ) as ApiResponse,
  }
}
